import React, { useCallback, useMemo } from 'react';

import { IconButton } from '@wix/wix-ui-tpa';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { useAppDispatch, useAppSelector } from '@/store';
import { minimizeWidget, selectOneChatSettings } from '@/features';
import Minus from '@/assets/icons/customMinimizeButton.svg';
import { EXPERIMENTS } from '@/constants';

import { iconsDict } from 'AiAssistantWidget/Settings/components';
import settingsParams from 'AiAssistantWidget/settingsParams';
import { useWidgetSettings } from 'AiAssistantWidget/Widget/contexts';
import { useAppExperiments } from 'AiAssistantWidget/experiments';

import { AIAvatarIcon } from '../svg';

import { st, classes } from './Header.st.css';

export const Header = ({ dialogId }: { dialogId?: string }) => {
  const { t } = useTranslation();
  const { experiments } = useAppExperiments();
  const settings = useWidgetSettings();
  const dispatch = useAppDispatch();
  const isOneChat = experiments.enabled(EXPERIMENTS.ONE_CHAT);
  const isAi = useAppSelector(selectOneChatSettings.isAi);

  const useAvatar = useMemo(
    () => settings.get(settingsParams.displayChatbotAvatar, 'primary'),
    [settings],
  );

  const { legacyName, oneChatName } = useMemo(
    () => ({
      legacyName: settingsParams.textHeader,
      oneChatName: isAi
        ? settingsParams.textHeader
        : settingsParams.manualChatTextHeader,
    }),
    [isAi],
  );
  const name = useMemo(
    () => settings.get(isOneChat ? oneChatName : legacyName),
    [isOneChat, oneChatName, legacyName, settings],
  );

  const icon = useMemo(() => {
    const iconKey = settings.get(settingsParams.minimizedButtonIcon, 'primary');

    return iconKey ? iconsDict[iconKey] : <AIAvatarIcon />;
  }, [settings]);

  const onClickMinimize = useCallback(() => {
    dispatch(minimizeWidget());
  }, [dispatch]);

  return (
    <div className={st(classes.root)}>
      <div className={st(classes.left)}>
        {useAvatar && <div className={st(classes.avatar)}>{icon}</div>}

        <h2 id={dialogId} className={st(classes.name)} title={name}>
          {name}
        </h2>
      </div>
      <div className={st(classes.right)}>
        <IconButton
          contentClassName={st(classes.minimizeButton)}
          icon={<Minus />}
          onClick={onClickMinimize}
          className={st(classes.minimizeButton)}
          aria-label={t('app.widget.ariallabel.minimize')}
        />
      </div>
    </div>
  );
};
